import { Controller } from "@hotwired/stimulus";

// Controller for the Notification Tracker debug tool.
// Displays recently created TierNotifications in an overlay.
// Usage: <div data-controller="notification-tracker">...</div>
export default class extends Controller {
  static targets = ["overlay", "list", "handle"];

  // Interval ID for polling
  pollingIntervalId = null;
  // Timestamp when the tracker was opened
  openedAtTimestamp = null;
  // State flag - starts closed again
  isOpen = false;
  // Properties for dragging
  isDragging = false;
  offsetX = 0;
  offsetY = 0;
  lenderClientId = null; // Store LenderClient ID

  connect() {
    // Only activate the tracker if we are on a value report page
    if (!window.location.pathname.startsWith("/value_report/")) {
      console.log(
        "Notification Tester: Not on a value report page, controller inactive."
      );
      // Optionally remove the element entirely if inactive?
      // this.element.remove();
      return;
    }

    console.log(
      "Notification Tester attempting to connect on Value Report page..."
    );
    document.addEventListener("keydown", this.handleKeydown);
    this.lenderClientId = this.element.dataset.lenderClientId;
    console.log(
      `[Tracker connect] Lender Client ID from data attribute: ${this.lenderClientId}`
    );
    if (!this.lenderClientId) {
      console.error("Notification Tester Error: Lender Client ID not found!");
      // Consider disabling if ID is crucial and missing
    }

    if (this.hasOverlayTarget) {
      // Ensure it starts hidden
      this.overlayTarget.style.display = "none";
      console.log("Notification Tester connected and overlay target found.");
    } else {
      console.error("Notification Tester Error: overlay target not found!");
    }
    // Drag methods are arrow functions, no binding needed
  }

  disconnect() {
    console.log("Notification Tracker disconnected");
    document.removeEventListener("keydown", this.handleKeydown);
    // Ensure attribute is false on disconnect
    document.body.dataset.notificationTrackerOpen = "false";
    // Clean up dragging listeners if controller disconnects while dragging
    if (this.isDragging) {
      document.removeEventListener("mousemove", this.dragMove);
      document.removeEventListener("mouseup", this.dragEnd);
    }
    this.stopPolling();
  }

  // Use arrow function for automatic `this` binding
  handleKeydown = (event) => {
    // Rule Verification: Project Context (Ctrl+Shift+D shortcut)
    if (event.ctrlKey && event.shiftKey && event.key === "D") {
      event.preventDefault();
      this.toggleTracker();
    }
  };

  toggleTracker() {
    this.isOpen = !this.isOpen;
    document.body.dataset.notificationTrackerOpen = this.isOpen.toString(); // Update body attribute
    if (this.isOpen) {
      this.startTracker();
    } else {
      this.stopTracker();
    }
  }

  startTracker() {
    console.log("Starting Notification Tracker");
    this.openedAtTimestamp = new Date().toISOString();
    const listElement = this.listTarget.querySelector("ul");
    if (listElement) listElement.innerHTML = "";
    this.overlayTarget.style.display = "block";
    this.fetchNotifications();
    this.startPolling();
  }

  stopTracker() {
    console.log("Stopping Notification Tracker");
    this.overlayTarget.style.display = "none";
    this.stopPolling();
    this.openedAtTimestamp = null;
  }

  startPolling() {
    // Rule Verification: Project Context (Polling every 3 seconds)
    this.pollingIntervalId = setInterval(() => {
      this.fetchNotifications();
    }, 3000);
  }

  stopPolling() {
    if (this.pollingIntervalId) {
      clearInterval(this.pollingIntervalId);
      this.pollingIntervalId = null;
    }
  }

  // Refactored fetchNotifications to use async/await
  async fetchNotifications() {
    if (
      !this.lenderClientId ||
      typeof this.openedAtTimestamp !== "string" ||
      this.openedAtTimestamp.length === 0
    ) {
      console.warn(
        "Notification Tracker: Not fetching, tracker closed, timestamp invalid, or Lender Client ID missing."
      );
      return;
    }

    const timestamp = this.openedAtTimestamp;
    const currentLenderClientId = this.lenderClientId;
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const url = `/debug/tier_notifications?since=${timestamp}&lender_client_id=${currentLenderClientId}`;

    try {
      const response = await fetch(url, {
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            ?.content,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      this.renderNotifications(responseData);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      // Decide how to handle fetch errors in UI - maybe append an error message?
      // For now, just log. If listTarget exists, we could update it.
      if (this.hasListTarget) {
        // Avoid replacing the whole list if there are existing items
        // Maybe add a temporary error indicator?
        console.error("Fetch failed, list not updated.");
        // this.listTarget.querySelector("ul").innerHTML = "<li>Error loading notifications.</li>"; // Old way
      }
    }
  }

  renderNotifications(responseData) {
    const listElement = this.listTarget.querySelector("ul");
    if (!listElement) return;

    // Get IDs of currently rendered items
    const existingLogIds = new Set(
      Array.from(listElement.querySelectorAll("li[data-log-id]")).map(
        (li) => li.dataset.logId
      )
    );

    // Filter for new items that are not already rendered
    const newItems = responseData.filter(
      (item) => !existingLogIds.has(item.log_id?.toString())
    );

    // Check for and remove placeholder if adding first item(s)
    const placeholder = listElement.querySelector("li.placeholder-message");
    if (placeholder && newItems.length > 0) {
      placeholder.remove();
    }

    if (newItems.length > 0) {
      const newAccordionItemsHtml = newItems
        .map((item, index) => {
          const logTimestamp = new Date(item.log_created_at).toLocaleString();
          const notificationId = item.notification_id || "N/A";
          const tier = item.notification_tier || "N/A";
          // Use a unique ID based on log_id if possible, otherwise fallback
          const uniqueHtmlId = `tracker-item-${
            item.log_id || Date.now() + index
          }`;

          let detailsHtml = '<table class="w-full text-left text-xs"><tbody>';
          const rows = [
            { label: "Log ID", value: item.log_id },
            { label: "Log Created", value: logTimestamp },
            {
              label: "Log Owner",
              value: `${item.log_owner_type} (${item.log_owner_id})`,
            },
            { label: "Log Notes", value: item.log_notes || "-" },
            { label: "TN ID", value: notificationId, border: true },
            { label: "TN Tier", value: tier },
            { label: "TN Category", value: item.notification_category || "-" },
            {
              label: "TN Created",
              value: item.notification_created_at
                ? new Date(item.notification_created_at).toLocaleString()
                : "-",
            },
          ];

          rows.forEach((row, i) => {
            const bgColor = i % 2 === 0 ? "bg-white" : "bg-gray-50";
            const borderClass = row.border ? "border-t border-gray-300" : "";
            const paddingClass = "py-1";
            detailsHtml += `<tr class="${bgColor}">
                            <td class="font-semibold pr-2 ${paddingClass} ${borderClass}">${row.label}:</td>
                            <td class="${paddingClass} ${borderClass}">${row.value}</td>
                          </tr>`;
          });
          detailsHtml += "</tbody></table>";

          // Add data-log-id to the li element
          return `
          <li class="border-b border-gray-300 last:border-b-0" data-log-id="${item.log_id}">
            <button type="button"
                    data-action="click->notification-tracker#toggleAccordionItem"
                    aria-expanded="false"
                    aria-controls="${uniqueHtmlId}-content"
                    class="flex items-center justify-between w-full py-1.5 text-left font-medium">
              <span>TN ID: ${notificationId} | Tier: ${tier} | Logged: ${logTimestamp}</span>
              <svg data-accordion-icon class="w-3 h-3 rotate-180 shrink-0" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 6">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5 5 1 1 5"/>
              </svg>
            </button>
            <div id="${uniqueHtmlId}-content" class="hidden py-2 px-3 bg-gray-100 border-t border-gray-300">
              ${detailsHtml}
            </div>
          </li>
        `;
        })
        .join("");

      // Append only the new items
      listElement.insertAdjacentHTML("beforeend", newAccordionItemsHtml);
    } else if (!placeholder && listElement.children.length === 0) {
      // If list is empty and there was no placeholder, add placeholder
      listElement.innerHTML = `<li class="text-gray-500 italic py-1.5 placeholder-message">Click tracked links/buttons to see notifications appear here.</li>`;
    } else if (
      responseData?.length === 0 &&
      !placeholder &&
      listElement.children.length === 0
    ) {
      // Initial load with empty data, add placeholder
      listElement.innerHTML = `<li class="text-gray-500 italic py-1.5 placeholder-message">Click tracked links/buttons to see notifications appear here.</li>`;
    }
  }

  toggleAccordionItem(event) {
    const clickedButton = event.currentTarget;
    const contentId = clickedButton.getAttribute("aria-controls");
    const targetContent = document.getElementById(contentId);
    const targetIcon = clickedButton.querySelector("[data-accordion-icon]");

    if (!targetContent || !targetIcon) return;

    const isOpening = targetContent.classList.contains("hidden");

    // Close other items
    const listElement = this.listTarget.querySelector("ul");
    if (listElement) {
      listElement
        .querySelectorAll('li > div[id$="-content"]')
        .forEach((contentDiv) => {
          if (
            contentDiv.id !== contentId &&
            !contentDiv.classList.contains("hidden")
          ) {
            contentDiv.classList.add("hidden");
            const siblingButton = contentDiv.previousElementSibling;
            if (siblingButton && siblingButton.tagName === "BUTTON") {
              siblingButton.setAttribute("aria-expanded", "false");
              const siblingIcon = siblingButton.querySelector(
                "[data-accordion-icon]"
              );
              if (siblingIcon) {
                siblingIcon.classList.add("rotate-180");
              }
            }
          }
        });
    }

    // Toggle the clicked item
    targetContent.classList.toggle("hidden", !isOpening);
    clickedButton.setAttribute("aria-expanded", isOpening);
    targetIcon.classList.toggle("rotate-180", !isOpening);
  }

  // --- Dragging Logic (using arrow functions) ---

  dragStart = (event) => {
    // Prevent default text selection behavior during drag
    event.preventDefault();
    // Only start drag if the target is the handle itself
    // Use currentTarget to ensure it's the element the listener is attached to (the handle)
    if (event.currentTarget !== this.handleTarget) return;

    this.isDragging = true;
    // Calculate initial offset from the element's top-left corner
    this.offsetX = event.clientX - this.overlayTarget.offsetLeft;
    this.offsetY = event.clientY - this.overlayTarget.offsetTop;

    // Add listeners to the document to track mouse movement globally
    document.addEventListener("mousemove", this.dragMove);
    document.addEventListener("mouseup", this.dragEnd);
  };

  dragMove = (event) => {
    if (!this.isDragging) return;

    // Calculate new position
    const newX = event.clientX - this.offsetX;
    const newY = event.clientY - this.offsetY;

    // Update element's position
    // Important: Remove fixed bottom/right positioning if dragging starts
    this.overlayTarget.style.bottom = "auto";
    this.overlayTarget.style.right = "auto";
    this.overlayTarget.style.left = `${newX}px`;
    this.overlayTarget.style.top = `${newY}px`;
  };

  dragEnd = () => {
    if (!this.isDragging) return;

    this.isDragging = false;
    // Remove global listeners
    document.removeEventListener("mousemove", this.dragMove);
    document.removeEventListener("mouseup", this.dragEnd);
  };

  // --- End Dragging Logic ---
}
